/**
 * Fast stock sort: fetches lightweight handle lists, then only current page cards.
 */
(function () {
  const FETCH_CHUNK_SIZE = 250;
  const HANDLES_AVAILABLE_VIEW = 'stock-available-handles';
  const HANDLES_UNAVAILABLE_VIEW = 'stock-unavailable-handles';
  const CARD_VIEW = 'grid-card';
  const CACHE_TTL = 10 * 60 * 1000;
  let isSorting = false;
  let sortVersion = 0;
  let handlesPromise = null;

  function getCurrentPage() {
    const page = Number(new URLSearchParams(window.location.search).get('page') || '1');
    return page > 0 ? page : 1;
  }

  function buildFetchParams() {
    const params = new URLSearchParams(window.location.search);
    params.delete('section_id');
    params.delete('view');
    return params;
  }

  function hasActiveFilters(params) {
    return [...params.keys()].some((key) => key.startsWith('filter.'));
  }

  function getGridContext(grid) {
    const params = buildFetchParams();
    const sortCacheKey = grid.dataset.sortCacheKey || '';
    const handlesCacheKey = `stock-handles:${grid.dataset.collectionUrl}:${sortCacheKey}`;
    const currentPage = getCurrentPage();
    const sortKey = `${currentPage}:${sortCacheKey}`;

    return {
      collectionUrl: grid.dataset.collectionUrl,
      productsPerPage: Number(grid.dataset.productsPerPage || '16'),
      includeUnavailable: grid.dataset.hideSoldOut !== 'true',
      params,
      handlesCacheKey,
      currentPage,
      sortKey,
    };
  }

  function gridNeedsSort(grid) {
    const { sortKey } = getGridContext(grid);
    return grid.dataset.sortedKey !== sortKey;
  }

  function parseHandles(text) {
    return text
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);
  }

  function readHandlesCache(handlesCacheKey) {
    try {
      const raw = sessionStorage.getItem(handlesCacheKey);
      if (!raw) return null;

      const data = JSON.parse(raw);
      if (!data.handles?.length || Date.now() - data.timestamp > CACHE_TTL) {
        sessionStorage.removeItem(handlesCacheKey);
        return null;
      }

      return data.handles;
    } catch (error) {
      return null;
    }
  }

  function writeHandlesCache(handlesCacheKey, handles) {
    try {
      sessionStorage.setItem(
        handlesCacheKey,
        JSON.stringify({
          handles,
          timestamp: Date.now(),
        })
      );
    } catch (error) {
      /* sessionStorage full */
    }
  }

  function readEmbeddedHandles() {
    const node = document.getElementById('CollectionStockHandles');
    if (!node) return null;

    try {
      const handles = JSON.parse(node.textContent);
      return Array.isArray(handles) && handles.length ? handles : null;
    } catch (error) {
      return null;
    }
  }

  async function fetchHandleChunks(collectionUrl, viewName, params, totalCount) {
    const fetchPages = Math.max(1, Math.ceil(totalCount / FETCH_CHUNK_SIZE));
    const requests = [];

    for (let page = 1; page <= fetchPages; page += 1) {
      const pageParams = new URLSearchParams(params);
      pageParams.set('page', String(page));
      pageParams.set('view', viewName);

      const query = pageParams.toString();
      const url = query ? `${collectionUrl}?${query}` : collectionUrl;
      requests.push(fetch(url).then((response) => (response.ok ? response.text() : '')));
    }

    const responses = await Promise.all(requests);
    return responses.flatMap((text) => parseHandles(text));
  }

  async function fetchSortedHandles(grid, version) {
    const embedded = readEmbeddedHandles();
    const { collectionUrl, includeUnavailable, params, handlesCacheKey } = getGridContext(grid);
    const cached = readHandlesCache(handlesCacheKey);

    if (cached) return cached;
    if (embedded) {
      writeHandlesCache(handlesCacheKey, embedded);
      return embedded;
    }

    const totalCount = Number(grid.dataset.productsCount || '0');
    const requests = [
      fetchHandleChunks(collectionUrl, HANDLES_AVAILABLE_VIEW, params, totalCount),
    ];

    if (includeUnavailable) {
      requests.push(fetchHandleChunks(collectionUrl, HANDLES_UNAVAILABLE_VIEW, params, totalCount));
    }

    const results = await Promise.all(requests);
    if (version !== sortVersion) return [];

    const handles = includeUnavailable ? results[0].concat(results[1]) : results[0];
    if (handles.length) writeHandlesCache(handlesCacheKey, handles);

    return handles;
  }

  function getSortedHandles(grid, version) {
    if (!handlesPromise || handlesPromise.version !== version) {
      handlesPromise = fetchSortedHandles(grid, version).then((handles) => {
        handlesPromise.handles = handles;
        return handles;
      });
      handlesPromise.version = version;
    }

    return handlesPromise;
  }

  async function fetchCardForHandle(handle) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}products/${handle}?view=${CARD_VIEW}`);
    return response.ok ? response.text() : '';
  }

  async function fetchCardsForHandles(handles) {
    const cards = await Promise.all(handles.map((handle) => fetchCardForHandle(handle)));
    return cards.filter(Boolean);
  }

  function renderCards(grid, cards, sortKey) {
    if (!cards.length) return false;

    grid.innerHTML = cards.join('');
    grid.dataset.sortedKey = sortKey;

    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(grid.innerHTML);
    }

    return true;
  }

  async function renderSortedPage(grid, handles, options = {}) {
    const { productsPerPage, currentPage, sortKey } = getGridContext(grid);
    const pageStart = (currentPage - 1) * productsPerPage;
    const pageHandles = handles.slice(pageStart, pageStart + productsPerPage);

    if (!pageHandles.length) return false;

    const cards = await fetchCardsForHandles(pageHandles);
    if (options.version !== sortVersion) return false;

    return renderCards(grid, cards, sortKey);
  }

  async function sortCollectionGrid(options = {}) {
    const grid = document.getElementById('product-grid');

    if (!grid || grid.dataset.stockSort !== 'true' || isSorting) return;

    const { handlesCacheKey, sortKey, params } = getGridContext(grid);

    if (hasActiveFilters(params)) return;
    const serverSorted = grid.dataset.serverSorted === 'true';
    const needsSort = gridNeedsSort(grid);

    if (!options.forceFetch && !needsSort) return;
    if (serverSorted && !options.background && !options.forceFetch && !needsSort) return;

    isSorting = true;
    const version = ++sortVersion;
    const collection = grid.closest('.collection');
    const hasHandlesCache = Boolean(readHandlesCache(handlesCacheKey) || readEmbeddedHandles());
    const showLoading = needsSort && !hasHandlesCache && !options.background && grid.children.length === 0;

    if (showLoading && collection) collection.classList.add('loading');

    try {
      const handles = await getSortedHandles(grid, version);
      if (version !== sortVersion || !handles.length) return;

      if (options.background && serverSorted) return;

      if (needsSort || options.forceFetch) {
        await renderSortedPage(grid, handles, { version });
      }
    } catch (error) {
      console.error('Collection stock sort failed:', error);
    } finally {
      if (showLoading && collection) collection.classList.remove('loading');
      isSorting = false;
    }
  }

  async function prefetchHandles() {
    const grid = document.getElementById('product-grid');
    if (!grid || grid.dataset.stockSort !== 'true') return;

    const version = sortVersion;
    await getSortedHandles(grid, version);
  }

  async function prefetchPageFromLink(link) {
    const grid = document.getElementById('product-grid');
    if (!grid || grid.dataset.stockSort !== 'true') return;

    try {
      const url = new URL(link.href);
      const page = Number(url.searchParams.get('page') || '1');
      const perPage = Number(grid.dataset.productsPerPage || '16');
      const version = sortVersion;
      const handles = await getSortedHandles(grid, version);

      if (!handles.length) return;

      const pageHandles = handles.slice((page - 1) * perPage, page * perPage);
      if (!pageHandles.length) return;

      await Promise.all(pageHandles.map((handle) => fetchCardForHandle(handle)));
    } catch (error) {
      /* ignore prefetch errors */
    }
  }

  function setupPaginationPrefetch() {
    document.querySelectorAll('.pagination a[href]').forEach((link) => {
      link.addEventListener(
        'mouseenter',
        () => {
          prefetchHandles();
          prefetchPageFromLink(link);
        },
        { passive: true }
      );
      link.addEventListener(
        'focus',
        () => {
          prefetchHandles();
          prefetchPageFromLink(link);
        },
        { passive: true }
      );
    });
  }

  function debounceSort(fn, wait) {
    let timeout;
    return function debounced(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  const debouncedSort =
    typeof debounce === 'function' ? debounce(sortCollectionGrid, 100) : debounceSort(sortCollectionGrid, 100);

  function init() {
    const grid = document.getElementById('product-grid');
    if (!grid || grid.dataset.stockSort !== 'true') return;

    if (hasActiveFilters(buildFetchParams())) return;

    if (grid.dataset.serverSorted === 'true') {
      sortCollectionGrid({ background: true });
    } else if (grid.dataset.stockSortPending !== 'true') {
      setTimeout(() => {
        if (gridNeedsSort(grid)) sortCollectionGrid();
      }, 300);
    } else {
      setTimeout(() => {
        if (gridNeedsSort(grid)) sortCollectionGrid();
      }, 800);
    }

    setupPaginationPrefetch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  const gridContainer = document.getElementById('ProductGridContainer');

  if (gridContainer) {
    const observer = new MutationObserver(() => {
      const grid = document.getElementById('product-grid');
      if (
        grid?.dataset.stockSort === 'true' &&
        !isSorting &&
        gridNeedsSort(grid) &&
        !hasActiveFilters(buildFetchParams())
      ) {
        debouncedSort();
        setupPaginationPrefetch();
      }
    });

    observer.observe(gridContainer, { childList: true, subtree: true });
  }
})();
